import { useEffect, useState, useCallback } from 'react';
import { RadioButton, Text, Flex, theme, Button } from '../../../2022-component-library';
import { useTestContext } from '../../../context/testContext';
import { optimaChartData } from '../../../helpers/data';
import styled from '@emotion/styled';
import { useMediaQuery } from '../../../helpers/useMediaQuery';
import { usePostMessage } from '../../../context/messageContext';

const SizeControlButton = styled(Button)`
  transition: all 400ms;
  opacity: ${(props) => (props.isShowing ? 1 : 0)};
  width: ${(props) => (props.isShowing ? '12em' : 0)};
  padding: ${(props) => (props.isShowing ? '0.75rem 1.5rem' : 0)};
  pointer-events: ${(props) => (props.isShowing ? 'auto' : 'none')};
`;

const ResponsiveOptimaChartTestOptician = ({ setCanProgress, eyeTested }) => {
  const { testData, setTestFieldValue, updateTestData } = useTestContext();
  const [startingSize, setStartingSize] = useState(testData['startingTextSize'] || null);
  const [selectedSize, setSelectedSize] = useState();
  const [neededMagnification, setNeededMagnification] = useState('');
  const smallScreen = useMediaQuery('(max-width: 700px)');
  const postMessage = usePostMessage();

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  const updateTextContents = useCallback(
    (textContents) => {
      postMessage({
        type: 'optimaTextContents',
        content: {
          textContents: textContents,
        },
      });
    },
    [postMessage],
  );

  useEffect(() => {
    setSelectedSize(startingSize.value);
    updateTextContents(startingSize);
  }, [startingSize, updateTextContents]);

  useEffect(() => {
    if (selectedSize && startingSize) {
      const magnification = getMagnificationByTextSize(selectedSize, startingSize);
      setNeededMagnification(magnification);
      setTestFieldValue({
        name: eyeTested,
        value: {
          textSizeRead: selectedSize,
          magnification: magnification,
        },
      });
      setCanProgress(true);
    }
  }, [selectedSize]);

  const getMagnificationByTextSize = (selectedSize, startingSize) => {
    var sizeDetails = startingSize.fontSizes.filter((obj) => {
      return obj.key === selectedSize;
    });
    return sizeDetails[0]?.magnification;
  };

  const changeSize = (newSize) => {
    setSelectedSize(newSize);
    var newSizeObject = optimaChartData.filter((obj) => {
      return obj.value.includes(newSize);
    });
    if (newSizeObject.length) {
      setStartingSize(newSizeObject[0]);
    }
    setTestFieldValue({
      name: eyeTested,
      value: {
        textSizeRead: selectedSize,
        magnification: neededMagnification,
      },
    });
  };

  return (
    <Flex width='100%' maxWidth='730px' column={!smallScreen}>
      <Flex width={smallScreen ? '75%' : '100%'} marginBottom='1rem'>
        <Flex
          column
          width='75vw'
          height='50vh'
          maxHeight='300px'
          padding={smallScreen ? '0.5rem' : '2rem 0.5rem 0rem 0.5rem'}
          overflowY='auto'
          marginRight='0.5rem'
        >
          {startingSize.fontSizes.map((option, index, array) => {
            return (
              <Flex centered key={option.key}>
                <Flex width='20vw' maxWidth='100px'>
                  <RadioButton
                    type='radio'
                    id={option.key}
                    value={option.key}
                    onChange={() => setSelectedSize(option.key)}
                    checked={option.key === selectedSize}
                    label={option.key}
                  />
                </Flex>
                <Flex
                  backgroundColor='#fafafa'
                  borderRadius='4px'
                  border='1px solid #d6e4ec'
                  padding='1rem'
                  marginBottom={index !== array.length - 1 ? '1rem' : 0}
                  width='60vw'
                  maxWidth='500px'
                >
                  <Text fontFamily='Poppins' fontSize='0.875rem'>
                    {option.content}
                  </Text>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
      <Flex width={smallScreen ? '25%' : '100%'} centered column={smallScreen} margin={!smallScreen && '2rem 0'}>
        <SizeControlButton
          onClick={() => changeSize(startingSize.navigation.previous)}
          backgroundColor={theme.colors.callToAction}
          isShowing={startingSize.navigation.previous}
          marginRight={!smallScreen && `${startingSize.navigation.next ? '1rem' : 0}`}
          marginBottom={smallScreen && `${startingSize.navigation.next ? '1rem' : 0}`}
        >
          <Flex centered height='0.75rem'>
            <Text fontSize='1.5rem' marginRight='0.5rem'>
              {'\u002b'}
            </Text>
            {`Bigger`}
          </Flex>
        </SizeControlButton>

        <SizeControlButton
          onClick={() => changeSize(startingSize.navigation.next)}
          backgroundColor={theme.colors.secondaryDark}
          isShowing={startingSize.navigation.next}
        >
          <Flex centered height='0.75rem'>
            <Text fontSize='1.25rem' marginRight='0.5rem'>
              {'\u002d'}
            </Text>
            {`Smaller`}
          </Flex>
        </SizeControlButton>
      </Flex>
    </Flex>
  );
};

export default ResponsiveOptimaChartTestOptician;
